// src/pages/ServicesValuationExpert.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import { Users, Euro } from "lucide-react";

const PURPOSES = [
  { value: "Préparer une vente", key: "sale" },
  { value: "Assurance", key: "insurance" },
  { value: "Succession / transmission", key: "succession" },
  { value: "Donation", key: "donation" },
  { value: "Autre (je précise ci-dessous)", key: "other" },
];

export default function ServicesValuationExpert() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, profile } = useAuth() || {};

  const artworkId = location?.state?.preselectArtworkId ?? null;

  const [email, setEmail] = useState("");
  const [purpose, setPurpose] = useState(PURPOSES[0].value);
  const [details, setDetails] = useState("");
  const [preferredContact, setPreferredContact] = useState("email");
  const [expertChoice, setExpertChoice] = useState("artkiv_selects");
  const [selectedExpertId, setSelectedExpertId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [artwork, setArtwork] = useState(null);
  const [experts, setExperts] = useState([]);
  const [loadingExperts, setLoadingExperts] = useState(false);

  useEffect(() => {
    if (!artworkId) return;
    supabase
      .from("artworks")
      .select("id, title, artist, year, image_url")
      .eq("id", artworkId)
      .single()
      .then(({ data }) => {
        if (data) setArtwork(data);
      });
  }, [artworkId]);

  useEffect(() => {
    const auto = profile?.email || user?.email || "";
    setEmail(auto);
  }, [user?.email, profile?.email]);

  useEffect(() => {
    if (expertChoice === "i_choose") fetchExperts();
  }, [expertChoice]);

  useEffect(() => {
    if (expertChoice !== "i_choose") setSelectedExpertId(null);
  }, [expertChoice]);

  async function fetchExperts() {
    setLoadingExperts(true);
    try {
      const { data, error } = await supabase
        .from("experts")
        .select("id, name, specialty, bio, organization, location, image_url, expert_user_id")
        .eq("is_public", true)
        .is("directory_opt_out_at", null)
        .not("directory_opt_in_at", "is", null)
        .order("name", { ascending: true });

      if (error) throw error;
      setExperts(data || []);
    } catch (err) {
      console.error("[ServicesValuationExpert] experts:", err);
    } finally {
      setLoadingExperts(false);
    }
  }

  const artworkLabel = useMemo(
    () => (artwork ? [artwork.title, artwork.artist].filter(Boolean).join(" — ") : artworkId),
    [artwork, artworkId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMsg("");

    try {
      const selectedExpert = expertChoice === "i_choose"
        ? experts.find((x) => x.id === selectedExpertId)
        : null;

      const { error } = await supabase.from("partner_requests").insert([
        {
          client_id: user?.id ?? null,
          artwork_id: artworkId,
          expert_id: selectedExpert?.id || null,
          type: "valuation",
          status: "pending",
          notes: details,
          payload: {
            purpose,
            details,
            artwork_id: artworkId,
            artwork_title: artwork?.title || null,
            artwork_artist: artwork?.artist || null,
            context: purpose,
            preferredContact,
            expertChoice,
            selectedExpertId: expertChoice === "i_choose" ? selectedExpertId : null,
            selectedExpertName: selectedExpert?.name || null,
            selectedExpertHasAccount: !!selectedExpert?.expert_user_id,
          },
        },
      ]);
      if (error) throw error;
      setDone(true);
    } catch (err) {
      console.error(err);
      setErrorMsg(t("servicesValuationPage.error.generic"));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" style={{ fontFamily: "Georgia, serif", backgroundColor: "#fafaf9" }}>
        <button onClick={() => navigate(-1)} className="text-sm mb-4" style={{ color: "#1a1a1a", textDecoration: "underline" }}>
          {t("servicesValuationPage.back")}
        </button>
        <h1 className="text-3xl font-semibold mb-3" style={{ color: "#1a1a1a" }}>
          {t("servicesValuationPage.success.title")}
        </h1>
        <p style={{ color: "#4a4a4a", lineHeight: "1.6" }}>
          <Trans i18nKey="servicesValuationPage.success.body" components={{ strong: <strong /> }} />
        </p>
        <button
          className="mt-6 px-4 py-2 rounded-md"
          style={{ backgroundColor: "#1a1a1a", color: "white" }}
          onClick={() => (artworkId ? navigate(`/artwork/${artworkId}`) : navigate("/my-collection"))}
        >
          {artworkId ? t("servicesValuationPage.success.backArtwork") : t("servicesValuationPage.success.backCollection")}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8" style={{ fontFamily: "Georgia, serif", backgroundColor: "#fafaf9" }}>
      <button onClick={() => navigate(-1)} className="text-sm mb-4" style={{ color: "#1a1a1a", textDecoration: "underline" }}>
        {t("servicesValuationPage.back")}
      </button>

      <h1 className="text-3xl font-semibold mb-2" style={{ color: "#1a1a1a" }}>
        {t("servicesValuationPage.title")}
      </h1>
      <p className="mb-6 max-w-2xl" style={{ color: "#4a4a4a", lineHeight: "1.6" }}>
        <Trans i18nKey="servicesValuationPage.intro" components={{ strong: <strong /> }} />
      </p>

      <div className="rounded-xl p-5 mb-6 flex items-start gap-3" style={{ border: "1px solid #e5e5e5", backgroundColor: "#fff7ed" }}>
        <Euro className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: "#c9a961" }} />
        <div>
          <h3 className="font-semibold mb-1" style={{ color: "#1a1a1a" }}>
            {t("servicesValuationPage.pricing.title")}
          </h3>
          <p className="text-sm" style={{ color: "#4a4a4a", lineHeight: "1.5" }}>
            <Trans i18nKey="servicesValuationPage.pricing.description" components={{ strong: <strong /> }} />
          </p>
        </div>
      </div>

      <div className="rounded-2xl p-6" style={{ border: "1px solid #e5e5e5", backgroundColor: "white" }}>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesValuationPage.artwork.label")}
              </label>
              <div className="text-sm">
                <span className="px-3 py-1 rounded" style={{ backgroundColor: "#fff7ed", border: "1px solid #d4d4d4", color: "#1a1a1a" }}>
                  {artworkLabel || t("servicesValuationPage.artwork.unknown")}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesValuationPage.purpose.label")}
              </label>
              <select value={purpose} onChange={(e) => setPurpose(e.target.value)} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }}>
                {PURPOSES.map((p) => (
                  <option key={p.key} value={p.value}>{t(`servicesValuationPage.purpose.options.${p.key}`)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                {t("servicesValuationPage.details.label")}
              </label>
              <textarea value={details} onChange={(e) => setDetails(e.target.value)} rows={5} placeholder={t("servicesValuationPage.details.placeholder")} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "#1a1a1a" }}>
                <Users className="w-4 h-4 inline mr-1" />
                {t("servicesValuationPage.expert.label")}
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="expertChoice" value="artkiv_selects" checked={expertChoice === "artkiv_selects"} onChange={(e) => setExpertChoice(e.target.value)} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{t("servicesValuationPage.expert.auto.title")}</div>
                    <div className="text-xs text-gray-500">{t("servicesValuationPage.expert.auto.description")}</div>
                  </div>
                </label>

                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="radio" name="expertChoice" value="i_choose" checked={expertChoice === "i_choose"} onChange={(e) => setExpertChoice(e.target.value)} className="mt-1" />
                  <div>
                    <div className="font-medium text-sm">{t("servicesValuationPage.expert.choose.title")}</div>
                    <div className="text-xs text-gray-500">{t("servicesValuationPage.expert.choose.description")}</div>
                  </div>
                </label>
              </div>

              {expertChoice === "i_choose" && (
                <div className="mt-3 p-3 rounded-lg bg-gray-50 border border-gray-200">
                  {loadingExperts ? (
                    <div className="text-sm text-gray-500">{t("servicesValuationPage.expert.loading")}</div>
                  ) : experts.length === 0 ? (
                    <div className="text-sm text-gray-500">{t("servicesValuationPage.expert.empty")}</div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {experts.map((expert) => (
                        <label key={expert.id} className="flex items-start gap-3 p-2 rounded cursor-pointer hover:bg-white transition">
                          <input type="radio" name="selectedExpert" value={expert.id} checked={selectedExpertId === expert.id} onChange={() => setSelectedExpertId(expert.id)} className="mt-1" />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{expert.name || t("servicesValuationPage.expert.generic")}</div>
                            <div className="text-xs text-gray-600">{expert.specialty}{expert.organization ? ` · ${expert.organization}` : ""}</div>
                            {!expert.expert_user_id && (
                              <div className="text-xs mt-1" style={{ color: "#c9a961" }}>
                                {t("servicesValuationPage.expert.noAccountHint")}
                              </div>
                            )}
                            {expert.bio && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{expert.bio}</div>}
                          </div>
                        </label>
                      ))}
                    </div>
                  )}
                  <button type="button" onClick={() => navigate("/experts")} className="mt-2 text-xs underline" style={{ color: "#c9a961" }}>
                    {t("servicesValuationPage.expert.directory")}
                  </button>
                </div>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                  {t("servicesValuationPage.contact.email")}
                </label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: "#1a1a1a" }}>
                  {t("servicesValuationPage.contact.preference")}
                </label>
                <select value={preferredContact} onChange={(e) => setPreferredContact(e.target.value)} className="w-full border rounded-md px-3 py-2" style={{ backgroundColor: "white", borderColor: "#d4d4d4" }}>
                  <option value="email">{t("servicesValuationPage.contact.methods.email")}</option>
                  <option value="phone">{t("servicesValuationPage.contact.methods.phone")}</option>
                  <option value="visio">{t("servicesValuationPage.contact.methods.video")}</option>
                </select>
              </div>
            </div>

            {errorMsg && <div className="text-sm" style={{ color: "#dc2626" }}>{errorMsg}</div>}

            <div className="pt-2">
              <button type="submit" disabled={isSubmitting || (expertChoice === "i_choose" && !selectedExpertId)} className="px-4 py-2 rounded-md disabled:opacity-60" style={{ backgroundColor: "#1a1a1a", color: "white" }}>
                {isSubmitting ? t("servicesValuationPage.submit.sending") : t("servicesValuationPage.submit.send")}
              </button>
            </div>
          </div>
        </form>
      </div>

      <p className="text-xs mt-4" style={{ color: "#6b7280" }}>
        <Trans i18nKey="servicesValuationPage.note" components={{ strong: <strong /> }} />
      </p>
    </div>
  );
}
